import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router'
import { authApiService } from '../services/authApiService'
import { ApiError } from '../services/BaseApiService'
import { colors, fontSize, radius, shadow, fontFamily, componentHeight, transition } from '../styles/theme'

/**
 * Email verification. A new sign-up lands here from register (or from a login attempt on an
 * unverified account), and can confirm their address either way the email offers:
 *  - the link carries ?token= and is verified automatically on mount;
 *  - the 6-digit code is entered by hand.
 * Either path, once it succeeds, sends the user to sign in. Verification deliberately does not
 * grant a session itself.
 */
export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const linkToken = searchParams.get('token') ?? ''
  const emailFromState = (location.state as { email?: string } | null)?.email ?? ''

  const [email, setEmail] = useState(emailFromState)
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [loading, setLoading] = useState(false)
  // 'verifying' while the link token is being checked; 'done' once any path succeeds.
  const [linkState, setLinkState] = useState<'idle' | 'verifying' | 'done'>(linkToken ? 'verifying' : 'idle')
  const linkTried = useRef(false)

  useEffect(() => {
    if (!linkToken || linkTried.current) return
    linkTried.current = true
    authApiService.verifyEmail(linkToken)
      .then(() => {
        setLinkState('done')
        setTimeout(() => navigate('/login'), 1500)
      })
      .catch(err => {
        setLinkState('idle')
        setError(err instanceof ApiError ? err.message : 'Verification link is invalid or expired.')
      })
  }, [linkToken, navigate])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(''); setNotice('')
    // No empty-email check: the email input is `required` (and absent only when the email came in route state).
    if (!/^\d{6}$/.test(code.trim())) { setError('Enter the 6-digit code from your email.'); return }
    setLoading(true)
    try {
      await authApiService.verifyEmailCode(email.trim(), code.trim())
      setLinkState('done')
      setTimeout(() => navigate('/login'), 1200)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verification failed.')
    } finally {
      setLoading(false)
    }
  }

  async function handleResend() {
    setError(''); setNotice('')
    if (!email.trim()) { setError('Enter your email first.'); return }
    setLoading(true)
    try {
      await authApiService.resendVerification(email.trim())
      setNotice('If the account exists and is unverified, a new email is on its way.')
    } catch {
      setNotice('If the account exists and is unverified, a new email is on its way.')
    } finally {
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', height: componentHeight.inputMd, padding: '0 12px',
    border: `1px solid ${colors.border}`, borderRadius: radius.md,
    fontSize: fontSize.sm, color: colors.text, background: colors.surface,
    boxSizing: 'border-box', outline: 'none', fontFamily: fontFamily.sans,
    transition: `border-color ${transition.fast}`,
  }
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: fontSize.sm, fontWeight: 500, color: colors.text,
    marginBottom: 5, letterSpacing: '-0.01em',
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: colors.surfaceTinted, padding: '24px 16px', fontFamily: fontFamily.sans,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
            <path d="M4 3h8l4 4v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" fill="#fff" opacity={0.9} />
            <path d="M12 3l4 4h-4V3z" fill="#fff" opacity={0.6} />
            <path d="M7 9h6M7 12h6M7 15h4" stroke="#0F9D92" strokeWidth={1.2} strokeLinecap="round" />
          </svg>
        </div>
        <span style={{ fontSize: '22px', fontWeight: 600, color: colors.text, letterSpacing: '-0.02em' }}>
          Doc<span style={{ color: colors.primary }}>morph</span>ia
        </span>
      </div>

      <div style={{
        background: colors.surface, borderRadius: radius.lg, border: `1px solid ${colors.border}`,
        boxShadow: shadow.md, padding: '32px 32px 28px', width: '100%', maxWidth: 400,
      }}>
        <div style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, letterSpacing: '-0.02em', marginBottom: 8 }}>
          Verify your email
        </div>

        {linkState === 'verifying' && (
          <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: 8 }}>
            Confirming your email…
          </div>
        )}

        {linkState === 'done' ? (
          <div style={{
            background: colors.successLight, color: colors.text,
            borderRadius: radius.md, padding: '14px 16px', fontSize: fontSize.sm, marginTop: 8,
          }}>
            Email verified. Taking you to sign in…
          </div>
        ) : (
          <>
            <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: 24 }}>
              Enter the 6-digit code we emailed you, or open the link in that email.
            </div>

            {error && (
              <div style={{
                background: colors.errorLight, color: colors.error, border: `1px solid ${colors.errorBorder}`,
                borderRadius: radius.md, padding: '10px 14px', fontSize: fontSize.sm, marginBottom: 16,
              }}>
                {error}
              </div>
            )}
            {notice && (
              <div style={{
                background: colors.surfaceTinted, color: colors.textSecondary, border: `1px solid ${colors.border}`,
                borderRadius: radius.md, padding: '10px 14px', fontSize: fontSize.sm, marginBottom: 16,
              }}>
                {notice}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {!emailFromState && (
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    style={inputStyle} required autoComplete="email" placeholder="you@company.com" />
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Verification code</label>
                <input inputMode="numeric" value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  style={{ ...inputStyle, letterSpacing: '0.3em', fontFamily: 'monospace' }}
                  required autoFocus placeholder="123456" maxLength={6} />
              </div>
              <button type="submit" disabled={loading}
                style={{
                  width: '100%', height: componentHeight.btnLg, background: colors.primary,
                  color: '#fff', border: 'none', borderRadius: radius.full, fontSize: fontSize.sm,
                  fontWeight: 500, cursor: 'pointer', marginTop: 8, fontFamily: fontFamily.sans,
                  transition: `background ${transition.fast}`,
                }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.background = colors.primaryHover }}
                onMouseLeave={e => { e.currentTarget.style.background = colors.primary }}>
                {loading ? 'Verifying…' : 'Verify email'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 16, fontSize: fontSize.sm, color: colors.textSecondary }}>
              Didn't get it?{' '}
              <button type="button" onClick={handleResend} disabled={loading}
                style={{ background: 'none', border: 'none', color: colors.primary, fontWeight: 500, cursor: 'pointer', padding: 0, fontFamily: fontFamily.sans, fontSize: fontSize.sm }}>
                Resend email
              </button>
            </div>
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: fontSize.sm, color: colors.textSecondary }}>
          <Link to="/login" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 500 }}>
            ← Back to sign in
          </Link>
        </div>
      </div>
    </div>
  )
}
